import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ShieldCheck, AlertTriangle, CheckCircle2, Upload, Trash2,
  ArrowRight, FileText, Building2, Package,
} from "lucide-react";

/**
 * iter72 · Public Secure Vendor Link page (`/v/{token}`).
 *
 * NO account required — the URL is the bearer secret. Renders a
 * mobile-friendly workspace where a vendor rep can:
 *   • Review each line item on the order
 *   • Edit quantity + drop notes back to the club
 *   • Upload documents (quotes, POs, catalog cuts, invoices)
 *   • Submit — locks the token and notifies the club
 *
 * Scope is intentionally items-only (per user 2026-08-01) — no price,
 * ETA, or accept/reject controls surface here.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

export default function VendorLinkPage() {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true, order: null, club: null, recipient: null,
    tokenState: null, error: null, errorCode: null,
  });
  const [edits, setEdits] = useState({}); // { line_id: { quantity, vendor_notes } }
  const [savingLine, setSavingLine] = useState(null);
  const [savedFlash, setSavedFlash] = useState({});
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitNote, setSubmitNote] = useState("");
  const [submitErr, setSubmitErr] = useState("");
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/public/vendor-link/${token}`);
      setState({
        loading: false,
        order: data.order,
        club: data.club,
        recipient: data.recipient,
        tokenState: data.token_state,
        error: null, errorCode: null,
      });
      // Hydrate edits from saved-so-far, so returning vendors don't lose work.
      const initial = {};
      (data.order.items || []).forEach((li) => {
        if (li.vendor_quantity != null || li.vendor_notes != null) {
          initial[li.line_id] = {
            quantity: li.vendor_quantity ?? li.quantity,
            vendor_notes: li.vendor_notes ?? "",
          };
        }
      });
      setEdits(initial);
      setDocs(data.token_state?.uploaded_docs || []);
      if (data.token_state?.submitted_at) setSubmitted(true);
    } catch (e) {
      setState({
        loading: false,
        error: e.response?.data?.detail || e.message,
        errorCode: e.response?.status || null,
        order: null, club: null, recipient: null, tokenState: null,
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const draftFor = (li) => edits[li.line_id] || {
    quantity: li.quantity, vendor_notes: "",
  };

  const setDraft = (line_id, patch) =>
    setEdits((e) => ({ ...e, [line_id]: { ...draftFor({ line_id, ...(e[line_id] || {}) }), ...patch } }));

  const saveLine = async (li) => {
    setSavingLine(li.line_id);
    try {
      const draft = draftFor(li);
      await api.patch(`/public/vendor-link/${token}/items/${li.line_id}`, {
        quantity: Number(draft.quantity),
        vendor_notes: draft.vendor_notes || "",
      });
      setSavedFlash((s) => ({ ...s, [li.line_id]: Date.now() }));
      setTimeout(() => setSavedFlash((s) => {
        const { [li.line_id]: _, ...rest } = s;
        return rest;
      }), 2200);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setSavingLine(null); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/public/vendor-link/${token}/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [...d, data.doc]);
    } catch (err) {
      setUploadErr(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submitAll = async () => {
    setSubmitBusy(true); setSubmitErr("");
    try {
      await api.post(`/public/vendor-link/${token}/submit`, {
        submitted_note: submitNote || null,
      });
      setSubmitted(true);
    } catch (e) {
      setSubmitErr(e.response?.data?.detail || e.message);
    } finally { setSubmitBusy(false); }
  };

  const editedCount = useMemo(() => Object.keys(edits).length, [edits]);

  // ---- Render states ----
  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F6F4EC] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1A422D]" size={28} />
      </div>
    );
  }
  if (state.error) return <ErrorScreen error={state.error} code={state.errorCode} />;
  if (submitted) return <SubmittedScreen order={state.order} club={state.club} docs={docs} />;

  const { order, club, recipient, tokenState } = state;
  const expiresPretty = tokenState?.expires_at
    ? new Date(tokenState.expires_at).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      }) : null;

  return (
    <div className="min-h-screen bg-[#F6F4EC]" data-testid="vendor-link-page">
      {/* Header — club branding + trust signal */}
      <header className="border-b border-[#E5E3D8] bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-10 py-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#1A422D]">
              <ShieldCheck size={12} /> Secure Vendor Link
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1A422D] mt-2">
              {club?.club_name}
            </h1>
            <div className="text-sm text-[#4A4A4A] mt-1">
              Order <span className="font-mono">{order.order_number}</span>
              {expiresPretty && <span className="text-[#7A7A7A]"> · Link expires {expiresPretty}</span>}
            </div>
          </div>
          {recipient?.vendor_company_name && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A]">Vendor</div>
              <div className="font-serif text-lg text-[#1A422D]">
                {recipient.vendor_company_name}
              </div>
              <div className="text-xs text-[#4A4A4A]">
                {recipient.representative_name} · {recipient.representative_email}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-8 space-y-8">
        {/* Intro copy */}
        <section className="bg-white border border-[#E5E3D8] p-6">
          <div className="overline text-[#B59A5A]">What we need from you</div>
          <p className="mt-2 text-sm text-[#1A1A1A] leading-relaxed">
            Please review each line item below and confirm the quantity you can fulfill.
            Add any notes (lead time, substitutes, minimums) directly on the line, and attach
            supporting documents (quotes, purchase orders, or catalog cuts) using the
            uploads panel. When you&rsquo;re done, click <em className="font-serif not-italic">Submit to Club</em>.
          </p>
        </section>

        {/* Line items */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-[#1A422D] inline-flex items-center gap-2">
              <Package size={16} /> Line Items ({order.items.length})
            </h2>
            {editedCount > 0 && (
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f33]">
                {editedCount} edited
              </span>
            )}
          </div>
          <div className="space-y-3">
            {order.items.map((li) => {
              const draft = draftFor(li);
              const saving = savingLine === li.line_id;
              const flashed = savedFlash[li.line_id];
              return (
                <div key={li.line_id}
                     data-testid={`vl-line-${li.line_id}`}
                     className="bg-white border border-[#E5E3D8] p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg text-[#1A422D]">{li.item_name}</div>
                      <div className="text-xs text-[#7A7A7A] mt-0.5">
                        {[li.brand, li.style_number, li.sku, li.category].filter(Boolean).join(" · ")}
                      </div>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-xs">
                        {li.size && <Field label="Size" value={li.size} />}
                        {li.color && <Field label="Color" value={li.color} />}
                        {li.logo_preference && <Field label="Logo" value={li.logo_preference} />}
                        {li.logo_location && <Field label="Location" value={li.logo_location} />}
                        {li.logo_color && <Field label="Logo Color" value={li.logo_color} />}
                        {li.thread_color && <Field label="Thread" value={li.thread_color} />}
                        {li.personalization && <Field label="Personalization" value={li.personalization} />}
                      </div>
                      {li.description && (
                        <p className="text-xs text-[#4A4A4A] mt-2 whitespace-pre-line">
                          {li.description}
                        </p>
                      )}
                      {li.notes && (
                        <div className="mt-2 text-xs bg-[#FBF6E8] border border-[#B59A5A] text-[#8a6f33] px-2 py-1.5">
                          <span className="uppercase tracking-[0.2em] text-[10px] mr-1">Note from club:</span>
                          {li.notes}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A7A7A]">Requested Qty</div>
                      <div className="font-serif text-3xl text-[#1A422D]">{li.quantity}</div>
                    </div>
                  </div>

                  {/* Vendor edits */}
                  <div className="mt-4 pt-4 border-t border-[#E5E3D8] grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <label className="block">
                      <span className="label-elegant block mb-1">Your fulfillable qty</span>
                      <input
                        type="number"
                        min="0"
                        value={draft.quantity ?? ""}
                        onChange={(e) => setDraft(li.line_id, { quantity: e.target.value })}
                        data-testid={`vl-qty-${li.line_id}`}
                        className="w-full border border-[#B59A5A] bg-[#FBF6E8] px-3 py-2 text-lg font-mono focus:outline-none focus:border-[#1A422D]"
                      />
                    </label>
                    <label className="block sm:col-span-3">
                      <span className="label-elegant block mb-1">Notes back to the club (lead time, sub, minimum)</span>
                      <input
                        type="text"
                        value={draft.vendor_notes ?? ""}
                        onChange={(e) => setDraft(li.line_id, { vendor_notes: e.target.value })}
                        data-testid={`vl-notes-${li.line_id}`}
                        placeholder="e.g. In stock, ships Friday · substitute in Style 8342-B · 12-unit minimum"
                        className="w-full border border-[#E5E3D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1A422D]"
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-3">
                    {flashed && (
                      <span className="text-[11px] text-[#1A422D] inline-flex items-center gap-1">
                        <CheckCircle2 size={12} /> Saved
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => saveLine(li)}
                      disabled={saving}
                      data-testid={`vl-save-${li.line_id}`}
                      className="inline-flex items-center gap-1.5 border border-[#1A422D] text-[#1A422D] hover:bg-[#1A422D] hover:text-[#F9F8F4] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50"
                    >
                      {saving ? "Saving…" : "Save Line"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Uploads */}
        <section>
          <h2 className="font-serif text-2xl text-[#1A422D] mb-3 inline-flex items-center gap-2">
            <FileText size={16} /> Attach Documents
          </h2>
          <div className="bg-white border border-[#E5E3D8] p-5">
            <p className="text-sm text-[#4A4A4A]">
              Attach any quotes, purchase orders, invoices, or catalog cuts related to this order.
              PDF, Word, Excel, and images up to 25MB.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                onChange={onUpload}
                data-testid="vl-upload-input"
                disabled={uploading}
                className="text-xs"
              />
              {uploading && <Loader2 size={14} className="animate-spin text-[#1A422D]" />}
            </div>
            {uploadErr && (
              <div className="mt-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2">
                <AlertTriangle size={12} /> {uploadErr}
              </div>
            )}
            {docs.length > 0 && (
              <ul className="mt-4 divide-y divide-[#E5E3D8] text-sm" data-testid="vl-doc-list">
                {docs.map((d) => (
                  <li key={d.doc_id} className="py-2 flex items-center justify-between gap-3">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <FileText size={14} className="text-[#8a6f33] shrink-0" />
                      <span className="truncate">{d.filename}</span>
                    </div>
                    <span className="text-[11px] text-[#7A7A7A] shrink-0">
                      {formatSize(d.size)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Submit */}
        <section className="bg-white border border-[#1A422D] p-6">
          <h2 className="font-serif text-2xl text-[#1A422D]">Submit to Club</h2>
          <p className="text-sm text-[#4A4A4A] mt-2">
            When you&rsquo;re done, submit your response. You&rsquo;ll be able to see this
            page again, but the club will be notified immediately.
          </p>
          <label className="block mt-4">
            <span className="label-elegant block mb-1">Overall note (optional)</span>
            <textarea
              value={submitNote}
              onChange={(e) => setSubmitNote(e.target.value)}
              data-testid="vl-submit-note"
              rows={3}
              className="w-full border border-[#E5E3D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1A422D]"
              placeholder="e.g. All items available. Total lead time 3 weeks."
            />
          </label>
          {submitErr && (
            <div className="mt-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-sm flex items-center gap-2">
              <AlertTriangle size={13} /> {submitErr}
            </div>
          )}
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-[#7A7A7A]">
              {editedCount} of {order.items.length} lines edited · {docs.length} document(s) attached
            </div>
            <button
              type="button"
              onClick={submitAll}
              disabled={submitBusy}
              data-testid="vl-submit"
              className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-6 py-3 text-xs uppercase tracking-[0.24em] hover:bg-[#0f1a14] disabled:opacity-50"
            >
              {submitBusy ? "Submitting…" : (<>Submit to Club <ArrowRight size={14} /></>)}
            </button>
          </div>
        </section>

        <footer className="text-center text-[10px] uppercase tracking-[0.24em] text-[#7A7A7A] pt-6 inline-flex items-center gap-2 justify-center w-full">
          <ShieldCheck size={11} /> Delivered securely by My Crest Club
        </footer>
      </main>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">{label}: </span>
      <span className="text-[#1A1A1A]">{value}</span>
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ErrorScreen({ error, code }) {
  const gone = code === 410;
  return (
    <div className="min-h-screen bg-[#F6F4EC] flex items-center justify-center px-6" data-testid="vl-error">
      <div className="max-w-md text-center bg-white border border-[#E5E3D8] p-10">
        <AlertTriangle size={28} className={gone ? "text-[#8a6f33] mx-auto" : "text-[#8a1a1a] mx-auto"} />
        <h1 className="font-serif text-2xl text-[#1A422D] mt-3">
          {gone ? "This link isn’t active" : "Link not found"}
        </h1>
        <p className="text-sm text-[#4A4A4A] mt-3">{error}</p>
        <p className="text-xs text-[#7A7A7A] mt-4">
          Please reach out to the club directly for a fresh link.
        </p>
      </div>
    </div>
  );
}

function SubmittedScreen({ order, club, docs }) {
  return (
    <div className="min-h-screen bg-[#F6F4EC] flex items-center justify-center px-6" data-testid="vl-submitted">
      <div className="max-w-lg text-center bg-white border border-[#1A422D] p-10">
        <CheckCircle2 size={32} className="text-[#1A422D] mx-auto" />
        <div className="overline text-[#B59A5A] mt-3">Response received</div>
        <h1 className="font-serif text-3xl text-[#1A422D] mt-2">
          Thank you.
        </h1>
        <p className="text-sm text-[#4A4A4A] mt-4">
          Your response to order <span className="font-mono">{order?.order_number}</span> at{" "}
          <strong>{club?.club_name}</strong> has been received.
        </p>
        {docs?.length > 0 && (
          <p className="text-xs text-[#7A7A7A] mt-2">
            {docs.length} document(s) attached.
          </p>
        )}
        <p className="text-xs text-[#7A7A7A] mt-6 inline-flex items-center gap-2">
          <Building2 size={12} /> The club will reach out with any follow-up.
        </p>
      </div>
    </div>
  );
}
